import Phaser from 'phaser';
import { inject } from '@vercel/analytics';
import { BootScene } from './scenes/BootScene';
import { MenuScene } from './scenes/MenuScene';
import { GameScene } from './scenes/GameScene';
import { UIScene } from './scenes/UIScene';
import { GAME_WIDTH, GAME_HEIGHT } from './config';

// Cookie Consent & Analytics Initialization
const COOKIE_KEY = 'stickdefence_cookie_consent';
const cookieBanner = document.getElementById('cookie-banner');
const btnAccept = document.getElementById('btn-cookie-accept');

if (!localStorage.getItem(COOKIE_KEY)) {
  // Show banner if not consented yet
  if (cookieBanner) cookieBanner.classList.remove('hidden');
}

if (btnAccept && cookieBanner) {
  btnAccept.addEventListener('click', () => {
    localStorage.setItem(COOKIE_KEY, 'true');
    cookieBanner.classList.add('hidden');
    // We already injected Analytics/AdSense below as requested by implied consent, 
    // so we just hide the banner.
  });
}

// Initialize Vercel Analytics
inject();

// Initialize Google AdSense
const adContainers = document.querySelectorAll('.support-banner .adsbygoogle');
if (import.meta.env.DEV) {
  // In development, do not call adsbygoogle.push to avoid invalid impressions in AdSense.
  // Instead, apply a styling hack to show a "TEST AD" placeholder.
  adContainers.forEach((el) => {
    const htmlEl = el as HTMLElement;
    htmlEl.style.display = 'flex';
    htmlEl.style.alignItems = 'center';
    htmlEl.style.justifyContent = 'center';
    htmlEl.style.backgroundColor = '#2a2a3e';
    htmlEl.style.color = '#888';
    htmlEl.style.border = '2px dashed #555';
    htmlEl.style.borderRadius = '4px';
    htmlEl.style.margin = '4px';
    htmlEl.style.fontFamily = 'monospace';
    htmlEl.style.fontSize = '12px';
    htmlEl.style.height = 'calc(100% - 8px)';
    htmlEl.style.width = 'calc(100% - 8px)';
    htmlEl.innerText = 'AdSense Block\n(Hidden in Dev)';
  });
} else {
  // In production, tell Google AdSense to fill the slots
  try {
    adContainers.forEach(() => {
      ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
    });
  } catch (err) {
    console.error('AdSense error:', err);
  }

  // AdBlock Detection Honeypot
  setTimeout(() => {
    const honeypot = document.createElement('div');
    honeypot.className = 'adsbox'; // Class targeted heavily by adblockers
    honeypot.style.width = '1px';
    honeypot.style.height = '1px';
    honeypot.style.position = 'absolute';
    honeypot.style.left = '-1000px';
    document.body.appendChild(honeypot);

    setTimeout(() => {
      // If height is 0, AdBlock is aggressively overriding styles
      if (honeypot.offsetHeight === 0) {
        adContainers.forEach((el) => {
          const htmlEl = el.parentElement as HTMLElement; // Get the .support-banner wrapper
          if (!htmlEl) return;

          htmlEl.innerHTML = `
            <div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; width:100%; text-align:center; padding: 10px; background: rgba(0,0,0,0.5); border-radius: 4px; border: 1px dashed #ff6b6b; color: #eee; font-size: 13px;">
              <span style="font-size: 20px; margin-bottom: 4px;">🛡️</span>
              <b>AdBlock Detected</b>
              <span style="margin-top: 4px; color: #aaa; font-size: 11px;">Please consider disabling it here to support the game's development!</span>
            </div>
          `;
        });
      }
      honeypot.remove();
    }, 150);
  }, 1000); // Give AdSense 1 second to breathe before checking
}

// Crisp text on HiDPI/Retina displays: patch Text factory to render at native resolution
const dpr = Math.min(window.devicePixelRatio || 1, 3);
if (dpr > 1) {
  const origText = Phaser.GameObjects.GameObjectFactory.prototype.text;
  Phaser.GameObjects.GameObjectFactory.prototype.text = function (
    this: Phaser.GameObjects.GameObjectFactory,
    x: number, y: number, text: string | string[], style?: Phaser.Types.GameObjects.Text.TextStyle
  ): Phaser.GameObjects.Text {
    const obj = origText.call(this, x, y, text, style);
    obj.setResolution(dpr);
    return obj;
  };
}

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  width: GAME_WIDTH,
  height: GAME_HEIGHT,
  parent: 'game-container',
  backgroundColor: '#87CEEB',
  scene: [BootScene, GameScene, UIScene],
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  render: {
    antialias: true,
    pixelArt: false,
    roundPixels: false,
  },
};

new Phaser.Game(config);

